import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TInsuranceCoverage } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { Row } from '@/web/components/Row/Row';
import { Text } from '@coverflex-tech/hypernova';
import { css } from './InsuranceUpgradePlanCardCoveragesTable.css';

export const InsuranceUpgradePlanCardCoveragesTable = ({
  coverages = [],
  reservedNonComparableRowCount = 0,
  reservedComparableRowCount = 0,
}: TInsuranceUpgradePlanCardCoveragesTableProps) => {
  const { t } = useTranslation();
  const [showAllCoverages, setShowAllCoverages] = useState(false);

  const {
    comparableCoverages,
    nonComparableCoverages,
    reservedComparableCoverageRows,
    reservedNonComparableCoverages,
  } = useMemo(() => {
    const comparableCoverages = coverages.filter(
      (coverage) => coverage.comparable
    );
    const nonComparableCoverages = coverages.filter(
      (coverage) => !coverage.comparable
    );
    const reservedNonComparableCoverages = new Array(
      reservedNonComparableRowCount > nonComparableCoverages.length
        ? reservedNonComparableRowCount - nonComparableCoverages.length
        : 0
    ).fill(null);
    const reservedComparableCoverageRows = new Array(
      reservedComparableRowCount > comparableCoverages.length
        ? reservedComparableRowCount - comparableCoverages.length
        : 0
    ).fill(null);
    return {
      comparableCoverages,
      nonComparableCoverages,
      reservedComparableCoverageRows,
      reservedNonComparableCoverages,
    };
  }, [coverages, reservedComparableRowCount, reservedNonComparableRowCount]);
  return (
    <div className={css.root}>
      <Row
        start={
          <Text bold>
            {t('dashboard.insurance.upgrade_plan.card.coverages_title')}
          </Text>
        }
      />
      {comparableCoverages.map((coverage, idx) => (
        <Row
          key={idx}
          customStyle={css.row}
          start={<Text className={css.startText}>{coverage.name}</Text>}
          end={
            <Text bold className={css.endText}>
              {coverage.value}
            </Text>
          }
        />
      ))}
      {reservedComparableCoverageRows.map((_, idx) => (
        <Row key={idx} start={<>&nbsp;</>} />
      ))}
      {showAllCoverages &&
        nonComparableCoverages.map((coverage, idx) => (
          <Row
            key={idx}
            start={<Text>{coverage.name}</Text>}
            end={<Text bold>{coverage.value}</Text>}
          />
        ))}

      {showAllCoverages &&
        reservedNonComparableCoverages.map((_, idx) => (
          <Row key={idx} start={<>&nbsp;</>} />
        ))}
      {nonComparableCoverages.length > 0 && (
        <div onClick={() => setShowAllCoverages(!showAllCoverages)}>
          <Text className={css.toggle} bold>
            {!showAllCoverages
              ? t(
                  'dashboard.insurance.upgrade_plan.card.coverages_expand_label'
                )
              : t(
                  'dashboard.insurance.upgrade_plan.card.coverages_collapse_label'
                )}
          </Text>
        </div>
      )}
    </div>
  );
};

export type TInsuranceUpgradePlanCardCoveragesTableProps = {
  coverages?: TInsuranceCoverage[];
  reservedComparableRowCount?: number;
  reservedNonComparableRowCount?: number;
  onToggleShowCoverages?: (expanded: boolean) => void;
};
